import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReaderBlock } from '@bookclub/shared';
import { getMediaCaptionText, MediaCaption } from '@/shared/components/blocks/MediaCaption';

interface ReaderImageGalleryProps {
  galleryId: string;
  blocks: ReaderBlock[];
  clubId: string;
  chapterId: string;
  onImageChange?: (payload: {
    clubId: string;
    chapterId: string;
    galleryId: string;
    blockId: string;
    index: number;
    totalCount: number;
  }) => void;
  onImageOpen?: (payload: {
    clubId: string;
    chapterId: string;
    galleryId: string;
    blockId: string;
    index: number;
    totalCount: number;
  }) => void;
}

function getImageUrl(block: ReaderBlock) {
  return block.mediaUrl || (block.payload['url'] as string) || '';
}

function getImageAlt(block: ReaderBlock) {
  return (block.payload['alt'] as string) || '';
}

export function ReaderImageGallery({
  galleryId,
  blocks,
  clubId,
  chapterId,
  onImageChange,
  onImageOpen,
}: ReaderImageGalleryProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isUserInteractingRef = useRef(false);
  const pushedViewerHistoryRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const totalCount = blocks.length;
  const activeBlock = blocks[activeIndex] ?? blocks[0];
  const fullscreenBlock = fullscreenIndex === null ? null : blocks[fullscreenIndex] ?? null;
  const galleryCaption = blocks.map(getMediaCaptionText).find((caption) => caption !== null) ?? null;
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < totalCount - 1;
  const fullscreenHasPrevious = fullscreenIndex !== null && fullscreenIndex > 0;
  const fullscreenHasNext = fullscreenIndex !== null && fullscreenIndex < totalCount - 1;

  useEffect(() => {
    if (activeIndex > totalCount - 1) {
      setActiveIndex(Math.max(totalCount - 1, 0));
    }
  }, [activeIndex, totalCount]);

  const closeFullscreen = useCallback((options?: { restoreHistory?: boolean }) => {
    setFullscreenIndex(null);
    setIsZoomed(false);

    if (options?.restoreHistory && pushedViewerHistoryRef.current) {
      pushedViewerHistoryRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    if (fullscreenIndex === null) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [fullscreenIndex]);

  useEffect(() => {
    if (fullscreenIndex === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeFullscreen({ restoreHistory: true });
        return;
      }

      if (event.key === 'ArrowLeft' && fullscreenHasPrevious) {
        event.preventDefault();
        setFullscreenIndex((current) => (current === null ? current : Math.max(0, current - 1)));
        setIsZoomed(false);
        return;
      }

      if (event.key === 'ArrowRight' && fullscreenHasNext) {
        event.preventDefault();
        setFullscreenIndex((current) => (current === null ? current : Math.min(totalCount - 1, current + 1)));
        setIsZoomed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeFullscreen, fullscreenHasNext, fullscreenHasPrevious, fullscreenIndex, totalCount]);

  useEffect(() => {
    if (fullscreenIndex === null) return;

    const handlePopState = () => {
      pushedViewerHistoryRef.current = false;
      closeFullscreen();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [closeFullscreen, fullscreenIndex]);

  useEffect(() => {
    return () => {
      pushedViewerHistoryRef.current = false;
    };
  }, []);

  const setActiveByUser = (nextIndex: number) => {
    const boundedIndex = Math.max(0, Math.min(nextIndex, totalCount - 1));
    if (boundedIndex === activeIndex) return;

    const nextBlock = blocks[boundedIndex];
    if (!nextBlock) return;

    setActiveIndex(boundedIndex);
    onImageChange?.({
      clubId,
      chapterId,
      galleryId,
      blockId: nextBlock.id,
      index: boundedIndex,
      totalCount,
    });
  };

  const openFullscreen = (nextIndex: number) => {
    const nextBlock = blocks[nextIndex];
    if (!nextBlock) return;

    setActiveIndex(nextIndex);
    setFullscreenIndex(nextIndex);
    setIsZoomed(false);

    if (!pushedViewerHistoryRef.current) {
      window.history.pushState({ readerImageGalleryId: galleryId }, '', window.location.href);
      pushedViewerHistoryRef.current = true;
    }

    onImageOpen?.({
      clubId,
      chapterId,
      galleryId,
      blockId: nextBlock.id,
      index: nextIndex,
      totalCount,
    });
  };

  const scrollToIndex = (nextIndex: number) => {
    isUserInteractingRef.current = true;
    setActiveByUser(nextIndex);
    const target = scrollerRef.current?.querySelector<HTMLElement>(`[data-gallery-index="${nextIndex}"]`);
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  const handleScroll = () => {
    if (!isUserInteractingRef.current) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollerCenter = scroller.getBoundingClientRect().left + scroller.clientWidth / 2;
    let nearestIndex = activeIndex;
    let nearestDistance = Number.POSITIVE_INFINITY;

    Array.from(scroller.querySelectorAll<HTMLElement>('[data-gallery-index]')).forEach((element) => {
      const rect = element.getBoundingClientRect();
      const elementCenter = rect.left + rect.width / 2;
      const distance = Math.abs(scrollerCenter - elementCenter);
      const index = Number(element.dataset.galleryIndex);

      if (Number.isFinite(index) && distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setActiveByUser(nearestIndex);
  };

  if (totalCount === 0 || !activeBlock) return null;

  return (
    <section
      data-testid={`image-gallery-${galleryId}`}
      aria-label={`Галерея изображений: ${activeIndex + 1} из ${totalCount}`}
      className="mx-1 rounded-[28px] border border-[rgba(139,111,72,0.16)] bg-[linear-gradient(135deg,rgba(255,252,246,0.96),rgba(239,225,200,0.7))] py-3 shadow-[0_14px_30px_rgba(62,48,23,0.08)] min-[430px]:mx-2"
    >
      <div className="flex items-center justify-end gap-3 px-3 min-[430px]:px-4">
        <span
          data-testid={`image-gallery-position-${galleryId}`}
          className="rounded-full bg-white/76 px-3 py-1 text-[12px] font-semibold tabular-nums text-[rgba(58,49,39,0.76)] shadow-[inset_0_0_0_1px_rgba(139,111,72,0.12)]"
        >
          {activeIndex + 1} / {totalCount}
        </span>
      </div>

      <div className="relative mt-3 px-2 min-[430px]:px-3">
        <button
          type="button"
          aria-label="Предыдущее изображение"
          disabled={!hasPrevious}
          onClick={(event) => {
            event.stopPropagation();
            scrollToIndex(activeIndex - 1);
          }}
          className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/62 text-[rgba(58,49,39,0.72)] shadow-[0_8px_18px_rgba(62,48,23,0.12),inset_0_0_0_1px_rgba(139,111,72,0.12)] backdrop-blur-sm transition enabled:hover:bg-white/86 disabled:cursor-not-allowed disabled:opacity-35"
        >
          ‹
        </button>

        <div
          ref={scrollerRef}
          data-testid={`image-gallery-scroller-${galleryId}`}
          className="flex min-w-0 snap-x gap-2 overflow-x-auto scroll-smooth pb-1"
          onPointerDown={() => {
            isUserInteractingRef.current = true;
          }}
          onTouchStart={() => {
            isUserInteractingRef.current = true;
          }}
          onScroll={handleScroll}
        >
          {blocks.map((block, index) => {
            const url = getImageUrl(block);
            const alt = getImageAlt(block);
            const isActive = index === activeIndex;

            return (
              <figure
                key={block.id}
                data-gallery-index={index}
                data-testid={`image-gallery-item-${block.id}`}
                data-active={isActive ? 'true' : 'false'}
                aria-label={`Изображение ${index + 1} из ${totalCount}${isActive ? ', активное' : ''}`}
                className={[
                  'w-[88%] shrink-0 snap-center rounded-[24px] border bg-white/62 p-1.5 transition min-[430px]:w-[82%]',
                  isActive
                    ? 'border-[rgba(194,112,46,0.78)] shadow-[0_12px_24px_rgba(129,83,35,0.16)] ring-2 ring-[rgba(232,151,76,0.26)]'
                    : 'border-white/70 shadow-[0_8px_18px_rgba(62,48,23,0.08)]',
                ].join(' ')}
              >
                {url ? (
                  <img
                    src={url}
                    alt={alt}
                    loading="lazy"
                    role="button"
                    tabIndex={0}
                    aria-label={`Открыть изображение ${index + 1} из ${totalCount}`}
                    className="aspect-[4/3] w-full rounded-[19px] object-cover"
                    onClick={(event) => {
                      event.stopPropagation();
                      openFullscreen(index);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      openFullscreen(index);
                    }}
                    onError={(event) => {
                      (event.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : null}
              </figure>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Следующее изображение"
          disabled={!hasNext}
          onClick={(event) => {
            event.stopPropagation();
            scrollToIndex(activeIndex + 1);
          }}
          className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/62 text-[rgba(58,49,39,0.72)] shadow-[0_8px_18px_rgba(62,48,23,0.12),inset_0_0_0_1px_rgba(139,111,72,0.12)] backdrop-blur-sm transition enabled:hover:bg-white/86 disabled:cursor-not-allowed disabled:opacity-35"
        >
          ›
        </button>
      </div>

      <MediaCaption
        caption={galleryCaption}
        className="px-5 pt-3 min-[430px]:px-6"
        testId={`image-gallery-caption-${galleryId}`}
      />

      {fullscreenBlock ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Просмотр изображения ${fullscreenIndex! + 1} из ${totalCount}`}
          data-testid={`image-fullscreen-viewer-${galleryId}`}
          className="fixed inset-0 z-[80] flex flex-col bg-[rgba(14,12,10,0.96)] text-white"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+1rem)]">
            <button
              type="button"
              aria-label="Закрыть просмотр изображения"
              onClick={() => closeFullscreen({ restoreHistory: true })}
              className="rounded-full bg-white/12 px-4 py-2 text-sm font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]"
            >
              Закрыть
            </button>
            <span
              data-testid={`image-fullscreen-position-${galleryId}`}
              className="rounded-full bg-white/12 px-3 py-1.5 text-sm font-semibold tabular-nums text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]"
            >
              {fullscreenIndex! + 1} / {totalCount}
            </span>
            <button
              type="button"
              aria-pressed={isZoomed}
              onClick={() => setIsZoomed((current) => !current)}
              className="rounded-full bg-white/12 px-4 py-2 text-sm font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]"
            >
              {isZoomed ? 'Уменьшить' : 'Увеличить'}
            </button>
          </div>

          <div
            className={[
              'relative flex min-h-0 flex-1 items-center pb-[calc(env(safe-area-inset-bottom)+1rem)]',
              isZoomed ? 'px-1 min-[430px]:px-2' : 'px-1',
            ].join(' ')}
          >
            {!isZoomed ? (
              <button
                type="button"
                aria-label="Предыдущее изображение в просмотре"
                disabled={!fullscreenHasPrevious}
                onClick={() => {
                  setFullscreenIndex((current) => (current === null ? current : Math.max(0, current - 1)));
                  setIsZoomed(false);
                }}
                className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/12 text-3xl text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] backdrop-blur-[1px] transition enabled:hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-30"
              >
                ‹
              </button>
            ) : null}

            <div
              data-testid={`image-fullscreen-frame-${galleryId}`}
              className={[
                'flex min-h-0 flex-1 items-center justify-center rounded-[24px] bg-black/22',
                isZoomed ? 'h-full overflow-auto overscroll-contain' : 'overflow-hidden',
              ].join(' ')}
              onDoubleClick={() => setIsZoomed((current) => !current)}
            >
              <img
                src={getImageUrl(fullscreenBlock)}
                alt={getImageAlt(fullscreenBlock)}
                data-testid={`image-fullscreen-image-${fullscreenBlock.id}`}
                className={[
                  'select-none rounded-[18px] object-contain transition-transform',
                  isZoomed ? 'max-w-none scale-150 cursor-grab' : 'max-h-full max-w-full cursor-zoom-in',
                ].join(' ')}
                draggable={false}
              />
            </div>

            {!isZoomed ? (
              <button
                type="button"
                aria-label="Следующее изображение в просмотре"
                disabled={!fullscreenHasNext}
                onClick={() => {
                  setFullscreenIndex((current) => (current === null ? current : Math.min(totalCount - 1, current + 1)));
                  setIsZoomed(false);
                }}
                className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/12 text-3xl text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] backdrop-blur-[1px] transition enabled:hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-30"
              >
                ›
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
